import { mainAPIUrl } from '../../../settings';

const baseURL = `${mainAPIUrl}/gservices_3/ss`;

/**
 * Fetches SmartSend order data by order ID
 * Endpoint: GET /gservices_3/ss/orders/v2/:orderId
 *
 * @param {string} orderId - The order ID
 * @param {string} tokenId - Optional authorization token (provided by consumer app via URL params)
 *                           Passed as Bearer token in Authorization header only
 * @returns {Promise<{result: Object, secureVerifyHeader: string}>}
 * @throws {Error} Throws error with response details if request fails
 */
export const getSmartSendOrderData = async (orderId, tokenId) => {
  const url = `${baseURL}/orders/v2/${orderId}`;

  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };

  // Add authorization header if tokenId is provided
  if (tokenId) {
    headers['Authorization'] = `Bearer ${tokenId}`;
  }

  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    const json = await response.json();
    const secureVerifyHeader = response.headers.get('X-Secure-Verify');

    // Check if response indicates an error (like SKU_EXPIRED)
    if (!response.ok || json.code === 'ERROR') {
      const error = new Error(json.message?.code || `API Error: ${response.status}`);
      error.response = json;
      throw error;
    }

    return {
      result: json.data,
      secureVerifyHeader
    };
  } catch (error) {
    // Handle timeout errors
    if (error.name === 'AbortError') {
      throw new Error('Request timeout: The server took too long to respond. Please try again.');
    }

    // Handle network errors, CORS, etc.
    // Network errors from fetch typically throw TypeError with messages containing
    // "fetch", "network", "Failed to", or "CORS". Check for these patterns to
    // distinguish network errors from other TypeErrors.
    if (error.name === 'TypeError') {
      const errorMessage = error.message?.toLowerCase() || '';
      const isNetworkError = 
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to') ||
        errorMessage.includes('cors') ||
        errorMessage.includes('load failed');
      
      if (isNetworkError) {
        throw new Error(`Network error: Unable to reach API server. Check if ${baseURL} is accessible.`);
      }
    }

    // Re-throw other errors
    throw error;
  } finally {
    // Always clear timeout to prevent it from firing after request completes/fails
    clearTimeout(timeoutId);
  }
};
